import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faStar, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import ScaleLoader from 'react-spinners/ScaleLoader';

import bumpIcon from '../images/ic_bump.png';
import superBumpIcon from '../images/ic_super_bump.png';

import BuyBumps from './BuyBumps';
import JoinPlus from './JoinPlus';
import ShoutoutCard from './ShoutoutCard';
import JukeboxVideoRow, { JukeboxRowAction } from './JukeboxVideoRow';
import LeaderTrophy from './LeaderTrophy';
import PlusFlair from './PlusFlair';
import PlayerCodeEntry from './PlayerCodeEntry';
import TriviaPlay from './TriviaPlay';

import * as JKYModel from '../custom-types';
import api from '../utils/Api';
import usePlayerUpdateHandler from '../utils/custom-hooks/PlayerUpdateHandler';
import useLeaderboard from '../utils/custom-hooks/LeaderboardHandler';
import { authContext } from '../contexts/AuthContext';
import { isFillerVideo } from '../utils/Filler';
import { trackCheckoutReturned, trackEvent } from '../utils/Analytics';

import './JukeboxView.scss';
import AppHeader from './AppHeader';
import { rememberJukebox } from '../utils/RecentJukeboxes';
import FooterContent from './FooterContent';

interface JukeboxViewProps {
  playerIdentifier: string;
}

type Tab = 'search' | 'queue' | 'favorites' | 'leaders';
type Notice = { tone: 'ok' | 'bad'; text: string };

// Two letters for someone with no profile picture, as on the TV.
const initials = (username: string) =>
  (username || '').replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase();

/**
 * The customer side of the web app: what a patron standing in the venue does with their
 * phone. It is the same four things the mobile app does - find a song, put it in the
 * queue, keep favourites, spend bumps - against the same endpoints, so a person can use
 * whichever they have to hand and see one account.
 *
 * Bumps can be bought here through Stripe Checkout when the server has Stripe keys (see
 * BuyBumps). The page never adds bumps itself: coming back from Stripe only refreshes the
 * counts, which the server raises once Stripe confirms the payment.
 *
 * The venue's own screen is /player/:id and is a different thing: it is the television on
 * the wall. This screen never plays anything.
 */
const JukeboxView = (props: JukeboxViewProps) => {
  const { playerIdentifier } = props;
  const context = useContext(authContext);
  const token = context.auth.token;
  const currentUser = context.currentUser;
  const location = useLocation();
  const navigate = useNavigate();

  // The hook carries updates in: the server pushes over a socket and polls as a fallback.
  // The local copy exists so this screen can also refresh itself the moment it changes
  // something, which is what covers a patron on venue wifi where the socket never opened.
  const { player: livePlayer, error: liveError, trivia } = usePlayerUpdateHandler(playerIdentifier);
  // During trivia night the game takes the screen. Adding songs, which play under the
  // questions, is one tap away, and so is getting back to the game.
  const triviaOn = !!(trivia && trivia.active);
  const [songsDuringTrivia, setSongsDuringTrivia] = useState(false);
  useEffect(() => setSongsDuringTrivia(false), [triviaOn]);
  const [player, setPlayer] = useState<JKYModel.Player>();
  const [actionError, setActionError] = useState<string>(null);

  // The hook owns the first fetch, so its error is the one that says a code is wrong.
  // Reading only our own would leave a bad code on the loading screen for good.
  const loadError = actionError || (liveError && liveError.message);

  const [tab, setTab] = useState<Tab>('search');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string>(null);

  const [favorites, setFavorites] = useState<any[]>(null);
  const [favoritesError, setFavoritesError] = useState<string>(null);
  const [loadingFavorites, setLoadingFavorites] = useState(false);

  const [busyKey, setBusyKey] = useState<string>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const noticeTimer = useRef<number>(null);

  const refreshPlayer = () =>
    api
      .players(`identifier/${playerIdentifier}`, 'GET', token, null)
      .then(response => {
        setPlayer(response);
        setActionError(null);
      })
      .catch(error => {
        setActionError(error.message);
      });

  const refreshUser = () =>
    api
      .users('me', 'GET', token, null)
      .then(response => context.updateCurrentUser(response))
      .catch(error => console.log('Could not refresh the current user: ' + error.message));

  // The player itself is the hook's job, including the first fetch. The bump counts are
  // not, and they are the one number on this screen that another device can change.
  useEffect(() => {
    refreshUser();
  }, [playerIdentifier]);

  useEffect(() => {
    if (livePlayer) {
      setPlayer(livePlayer);
    }
  }, [livePlayer]);

  // Remembered so home can offer it again next time without the code.
  const playerName = player && player.name;
  useEffect(() => {
    if (playerName) {
      rememberJukebox(playerIdentifier, playerName);
    }
  }, [playerIdentifier, playerName]);

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  // Back from Stripe. The webhook that adds the bumps can land a moment after the buyer
  // does, so the counts are re-read for a little while. The flag comes off the address so
  // a reload does not say it again.
  useEffect(() => {
    const outcome = new URLSearchParams(location.search).get('bumps');
    if (outcome !== 'paid' && outcome !== 'cancelled') {
      return;
    }
    navigate(location.pathname, { replace: true });
    trackCheckoutReturned(outcome);
    if (outcome === 'cancelled') {
      showNotice('bad', 'Payment cancelled. Nothing was charged.');
      return;
    }
    showNotice('ok', 'Payment received. Your bumps will show here in a moment.');
    let tries = 0;
    const poll = window.setInterval(() => {
      tries += 1;
      refreshUser();
      if (tries >= 8) {
        window.clearInterval(poll);
      }
    }, 2500);
    return () => window.clearInterval(poll);
  }, [location.search]);

  const showNotice = (tone: 'ok' | 'bad', text: string) => {
    window.clearTimeout(noticeTimer.current);
    setNotice({ tone, text });
    noticeTimer.current = window.setTimeout(() => setNotice(null), 5000);
  };

  // A search result has no database id, only a YouTube one; a queue video has both. Keying
  // on either keeps one "this row is busy" value working for all three lists.
  const rowKey = (video: any) => `${video.id || ''}:${video.thirdPartyId}`;

  // The column is a simple-array, so an account that has never favourited anything can come
  // back as [''] rather than [].
  const favoriteIds: string[] = ((currentUser && currentUser.favoriteVideos) || []).filter(Boolean);

  const availableBumps = (currentUser && currentUser.availableBumps) || 0;
  const availableSuperBumps = (currentUser && currentUser.availableSuperBumps) || 0;
  const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = searchValue.trim();
    if (!value) {
      return;
    }
    setSearching(true);
    setSearchError(null);
    try {
      // playerId lets the server mark songs this venue's house rules will refuse.
      const results = await api.queueVideos('search', 'POST', token, {
        searchValue: value,
        playerId: player ? player.id : undefined,
      });
      setSearchResults(results);
      trackEvent('search', { search_term: value, player_code: playerIdentifier });
    } catch (error) {
      setSearchResults(null);
      setSearchError(error.message);
    } finally {
      setSearching(false);
    }
  };

  const loadFavorites = async () => {
    setLoadingFavorites(true);
    setFavoritesError(null);
    try {
      const results = await api.users('myFavorites', 'GET', token, null);
      setFavorites(results);
    } catch (error) {
      setFavorites(null);
      setFavoritesError(error.message);
    } finally {
      setLoadingFavorites(false);
    }
  };

  useEffect(() => {
    if (tab === 'favorites' && favorites === null && !loadingFavorites && !favoritesError) {
      loadFavorites();
    }
  }, [tab]);

  const addToQueue = async (video: any) => {
    if (!player) {
      return;
    }
    setBusyKey(rowKey(video));
    try {
      await api.players('addVideo', 'POST', token, {
        playerId: player.id,
        thirdPartyId: video.thirdPartyId,
        url: video.url,
        title: video.title,
        description: video.description,
        duration: video.duration,
        viewCount: video.viewCount,
        thumbnail: video.thumbnail,
      });
      showNotice('ok', `Added ${video.title} to the queue.`);
      trackEvent('song_added', { player_code: playerIdentifier, video_id: video.thirdPartyId });
      await refreshPlayer();
    } catch (error) {
      // The API's refusals here are written for a patron to read - the per-person limit and
      // the no-duplicates rule both come back as sentences - so they are shown as they are.
      showNotice('bad', error.message);
    } finally {
      setBusyKey(null);
    }
  };

  const removeFromQueue = async (video: any) => {
    if (!player) {
      return;
    }
    setBusyKey(rowKey(video));
    try {
      await api.players('removeVideo', 'POST', token, { playerId: player.id, videoId: video.id });
      showNotice('ok', 'Removed from the queue.');
      await refreshPlayer();
    } catch (error) {
      showNotice('bad', error.message);
    } finally {
      setBusyKey(null);
    }
  };

  const spendBump = async (video: any, tier: 'bump' | 'superBump') => {
    setBusyKey(rowKey(video));
    try {
      await api.queueVideos(tier, 'POST', token, { videoId: video.id });
      showNotice('ok', tier === 'superBump' ? 'Super bumped.' : 'Bumped up one place.');
      trackEvent(tier === 'superBump' ? 'super_bump_used' : 'bump_used', { player_code: playerIdentifier });
      await Promise.all([refreshPlayer(), refreshUser()]);
    } catch (error) {
      showNotice('bad', error.message);
    } finally {
      setBusyKey(null);
    }
  };

  const toggleFavorite = async (video: any) => {
    const id = video.thirdPartyId;
    const isFavorite = favoriteIds.indexOf(id) > -1;
    const next = isFavorite ? favoriteIds.filter(f => f !== id) : [...favoriteIds, id];

    setBusyKey(rowKey(video));
    try {
      const user = await api.users('update', 'POST', token, { favoriteVideos: next });
      context.updateCurrentUser(user);
      if (favorites) {
        setFavorites(isFavorite ? favorites.filter(f => f.thirdPartyId !== id) : [...favorites, video]);
      }
      showNotice('ok', isFavorite ? 'Removed from your favorites.' : 'Saved to your favorites.');
      trackEvent(isFavorite ? 'favorite_removed' : 'favorite_added', { video_id: id });
    } catch (error) {
      showNotice('bad', error.message);
    } finally {
      setBusyKey(null);
    }
  };

  const favoriteAction = (video: any): JukeboxRowAction => {
    const isFavorite = favoriteIds.indexOf(video.thirdPartyId) > -1;
    return {
      key: 'favorite',
      label: isFavorite ? 'Favorited' : 'Favorite',
      icon: faStar,
      active: isFavorite,
      disabled: busyKey === rowKey(video),
      onClick: () => toggleFavorite(video),
    };
  };

  const addAction = (video: any): JukeboxRowAction => ({
    key: 'add',
    label: 'Add to queue',
    icon: faPlus,
    disabled: !player || busyKey === rowKey(video) || !!video.blockedReason,
    onClick: () => addToQueue(video),
  });

  // The venue's house rules, set from its control panel, so a refusal is no surprise.
  const maxSongSeconds = (player && Number(player.maxSongSeconds)) || 0;
  const houseRules = [
    maxSongSeconds > 0 ? `Songs up to ${Math.floor(maxSongSeconds / 60)}:${String(maxSongSeconds % 60).padStart(2, '0')}` : null,
    player && player.blockExplicit ? 'No explicit songs' : null,
  ].filter(Boolean);

  const queueVideos: any[] = (player && player.queue && player.queue.videos) || [];
  // The filler is not a song, so the queue reads as empty while it is on.
  const nowPlaying = player && !isFillerVideo(player.nowPlayingVideo) ? player.nowPlayingVideo : null;
  const isPlayerOwner = !!(player && player.user && currentUser && player.user.id === currentUser.id);

  // The song leaderboard, when the owner has it on. Its tab goes away if they turn it off.
  const showLeaderboard = !!(player && player.showLeaderboard);
  const leaders = useLeaderboard(player);
  useEffect(() => {
    if (tab === 'leaders' && player && !showLeaderboard) {
      setTab('search');
    }
  }, [showLeaderboard]);

  const queueActions = (video: any): JukeboxRowAction[] => {
    const busy = busyKey === rowKey(video);
    const isMine = !!(video.owner && currentUser && video.owner.id === currentUser.id);
    const actions: JukeboxRowAction[] = [
      {
        key: 'bump',
        label: 'Bump',
        tier: 'bump',
        disabled: busy || video.isSuperBump || availableBumps <= 0,
        onClick: () => spendBump(video, 'bump'),
      },
      {
        key: 'superBump',
        label: 'Super bump',
        tier: 'superBump',
        disabled: busy || video.isSuperBump || availableSuperBumps <= 0,
        onClick: () => spendBump(video, 'superBump'),
      },
      favoriteAction(video),
    ];
    if (isMine || isPlayerOwner) {
      actions.push({
        key: 'remove',
        label: 'Remove',
        icon: faMinus,
        disabled: busy,
        onClick: () => removeFromQueue(video),
      });
    }
    return actions;
  };

  if (!player && !loadError) {
    return (
      <div className="jukebox-loading">
        <h1>Connecting you to the jukebox</h1>
        <ScaleLoader cssOverride={{ display: 'flex' }} height={35} width={4} radius={2} color={'#fff'} loading={true} />
      </div>
    );
  }

  return (
    <div className="jukebox">
      <AppHeader />

      <div className="jukebox-body">
        {!!loadError && !player && (
          <div className="jukebox-panel">
            <p className="jukebox-empty">
              We could not find a jukebox with the code {playerIdentifier.toUpperCase()}. Check the code on the
              TV screen and try again.
            </p>
            {/* The box goes here rather than only on the home screen, because this is the
                moment somebody knows their code was wrong and is looking at the right one. */}
            <PlayerCodeEntry />
            <Link className="jukebox-primary-link" to="/home">
              Back to home
            </Link>
          </div>
        )}

        {!!player && triviaOn && !songsDuringTrivia && (
          <TriviaPlay
            trivia={trivia}
            playerIdentifier={playerIdentifier}
            venueName={player.name}
            token={token}
            currentUser={currentUser}
            onAddSongs={() => setSongsDuringTrivia(true)}
          />
        )}

        {!!player && (!triviaOn || songsDuringTrivia) && (
          <div className="jukebox-panel">
            {triviaOn && (
              <button type="button" className="jukebox-trivia-back" onClick={() => setSongsDuringTrivia(false)}>
                Trivia is on. Back to the game
              </button>
            )}
            <div className="jukebox-player">
              <div className="jukebox-player-name">{player.name}</div>
              <div className="jukebox-player-code">{playerIdentifier.toUpperCase()}</div>
            </div>

            {/* Jookable's own admins can run any player. The server checks too. */}
            {!!currentUser && currentUser.role === 'admin' && !isPlayerOwner && (
              <Link className="jukebox-primary-link" to={`/control/${playerIdentifier}`}>
                Open its control panel as admin
              </Link>
            )}

            <div className="jukebox-wallet">
              <span className="jukebox-wallet-item">
                <img src={bumpIcon} alt="" /> {plural(availableBumps, 'bump')}
              </span>
              <span className="jukebox-wallet-item">
                <img src={superBumpIcon} alt="" /> {plural(availableSuperBumps, 'super bump')}
              </span>
            </div>

            <BuyBumps
              token={token}
              returnPath={location.pathname}
              playerIdentifier={playerIdentifier}
              onError={message => showNotice('bad', message)}
            />

            <ShoutoutCard
              token={token}
              playerIdentifier={playerIdentifier}
              mode={player.shoutoutMode}
              returnPath={location.pathname}
              onNotice={showNotice}
            />

            <JoinPlus token={token} returnPath={location.pathname} onNotice={showNotice} onJoined={refreshUser} />

            {houseRules.length > 0 && <div className="jukebox-rules">House rules: {houseRules.join(' · ')}</div>}

            <div className="jukebox-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'search'}
                className={tab === 'search' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                onClick={() => setTab('search')}
              >
                Search
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'queue'}
                className={tab === 'queue' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                onClick={() => setTab('queue')}
              >
                Queue ({queueVideos.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'favorites'}
                className={tab === 'favorites' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                onClick={() => setTab('favorites')}
              >
                Favorites
              </button>
              {showLeaderboard && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'leaders'}
                  className={tab === 'leaders' ? 'jukebox-tab is-active' : 'jukebox-tab'}
                  onClick={() => setTab('leaders')}
                >
                  Top 5
                </button>
              )}
            </div>

            {!!notice && <div className={`jukebox-notice jukebox-notice-${notice.tone}`}>{notice.text}</div>}

            {tab === 'search' && (
              <div>
                <form className="jukebox-search" onSubmit={handleSearch}>
                  <input
                    type="search"
                    value={searchValue}
                    placeholder="Search for a song"
                    aria-label="Search for a song"
                    onChange={e => setSearchValue(e.target.value)}
                  />
                  <button type="submit" disabled={searching || !searchValue.trim()}>
                    <FontAwesomeIcon icon={faSearch} /> {searching ? 'Searching' : 'Search'}
                  </button>
                </form>

                {!!searchError && <p className="jukebox-error">{searchError}</p>}

                {!searchError && searchResults === null && (
                  <p className="jukebox-empty">Search for a song and add it to the queue.</p>
                )}

                {!!searchResults && searchResults.length === 0 && (
                  <p className="jukebox-empty">Nothing came back for that. Try different words.</p>
                )}

                {!!searchResults && searchResults.length > 0 && (
                  <ul className="jukebox-list">
                    {searchResults.map(video => (
                      <JukeboxVideoRow
                        key={rowKey(video)}
                        video={video}
                        note={video.blockedReason}
                        actions={[addAction(video), favoriteAction(video)]}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === 'queue' && (
              <div>
                {!nowPlaying && queueVideos.length === 0 && (
                  <p className="jukebox-empty">Empty Queue</p>
                )}

                <ul className="jukebox-list">
                  {!!nowPlaying && (
                    <JukeboxVideoRow
                      key={rowKey(nowPlaying)}
                      video={nowPlaying}
                      eyebrow="Now playing"
                      isBump={nowPlaying.isBump}
                      isSuperBump={nowPlaying.isSuperBump}
                      actions={[favoriteAction(nowPlaying)]}
                    />
                  )}
                  {queueVideos.map((video, index) => (
                    <JukeboxVideoRow
                      key={rowKey(video)}
                      video={video}
                      eyebrow={index === 0 ? 'Up next' : undefined}
                      isBump={video.isBump}
                      isSuperBump={video.isSuperBump}
                      actions={queueActions(video)}
                    />
                  ))}
                </ul>
              </div>
            )}

            {tab === 'favorites' && (
              <div>
                {loadingFavorites && <p className="jukebox-empty">Loading your favorites.</p>}
                {!!favoritesError && <p className="jukebox-error">{favoritesError}</p>}
                {!loadingFavorites && !favoritesError && favorites && favorites.length === 0 && (
                  <p className="jukebox-empty">Tap the star on any song to keep it here.</p>
                )}
                {!!favorites && favorites.length > 0 && (
                  <ul className="jukebox-list">
                    {favorites.map(video => (
                      <JukeboxVideoRow
                        key={rowKey(video)}
                        video={video}
                        actions={[addAction(video), favoriteAction(video)]}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === 'leaders' && showLeaderboard && (
              <div>
                <p className="jukebox-empty">Most songs played here in the last 7 days.</p>
                {leaders.length === 0 && <p className="jukebox-empty">No songs yet this week. Be the first.</p>}
                {leaders.length > 0 && (
                  <ol className="jukebox-leaders">
                    {leaders.map((leader, index) => (
                      <li
                        key={leader.id}
                        className={
                          currentUser && leader.id === currentUser.id ? 'jukebox-leader is-you' : 'jukebox-leader'
                        }
                      >
                        <LeaderTrophy index={index} className="jukebox-leader-rank" />
                        <PlusFlair isPlus={leader.isPlus}>
                          {leader.profilePicUrl ? (
                            <img className="jukebox-leader-avatar" src={leader.profilePicUrl} alt="" />
                          ) : (
                            <span className="jukebox-leader-avatar" aria-hidden="true">
                              {initials(leader.username)}
                            </span>
                          )}
                        </PlusFlair>
                        <span className="jukebox-leader-name">{leader.username}</span>
                        <span className="jukebox-leader-plays">{plural(leader.plays, 'song')}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="footer">
        <FooterContent />
      </div>
    </div>
  );
};

export default JukeboxView;
